import { useEffect, useRef } from "react";

/* Compaction-loop mark, ported verbatim from the Claude Design mock: a canvas
 * storyboard of dots animating lattice → orbit ring → single collapsed centre
 * dot, on a loop. Used on the Overview loading screen. */
export function Orb({
  size = 60,
  color = "#F4A2B9",
}: {
  size?: number;
  color?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;

    // getContext throws in environments without canvas support (jsdom/tests);
    // degrade to a blank canvas rather than crashing the host.
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = cv.getContext("2d");
    } catch {
      return;
    }
    if (!ctx) return;

    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const W = size,
      H = size;
    cv.width = W * DPR;
    cv.height = H * DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    const S = Math.min(W, H);
    const L = S * 0.3; // lattice half-extent
    const RR = S * 0.34; // orbit radius
    const N = 9;
    // keyframe positions per dot: lattice grid, orbit ring, collapsed centre
    const lattice: { x: number; y: number }[] = [];
    const ring: { x: number; y: number }[] = [];
    for (let i = 0; i < N; i++) {
      const r = Math.floor(i / 3),
        c = i % 3;
      lattice.push({ x: (c - 1) * L, y: (r - 1) * L });
      const a = -Math.PI / 2 + (i / N) * Math.PI * 2;
      ring.push({ x: Math.cos(a) * RR, y: Math.sin(a) * RR });
    }
    // X-lattice diagonal connections (between 2x2 cells of the 3x3 grid)
    const diagonals: [number, number][] = [];
    for (let r = 0; r < 2; r++)
      for (let c = 0; c < 2; c++) {
        const tl = r * 3 + c,
          tr = r * 3 + c + 1,
          bl = (r + 1) * 3 + c,
          br = (r + 1) * 3 + c + 1;
        diagonals.push([tl, br], [tr, bl]);
      }
    const T = 5.4;
    const ease = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const rgb = (() => {
      const h = color.replace("#", "");
      return (
        parseInt(h.substr(0, 2), 16) +
        "," +
        parseInt(h.substr(2, 2), 16) +
        "," +
        parseInt(h.substr(4, 2), 16)
      );
    })();
    let raf = 0;
    let t0: number | null = null;
    function draw(now: number) {
      if (!ctx) return;

      if (t0 === null) t0 = now;

      const el = (now - t0) / 1000;
      const u = (el % T) / T;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.lineCap = "round";
      const spin = el * 0.5;

      // segment blends
      let toRing = 0,
        toCentre = 0,
        back = 0;
      if (u < 0.28) {
        toRing = 0;
      } else if (u < 0.5) {
        toRing = ease((u - 0.28) / 0.22);
      } else if (u < 0.72) {
        toRing = 1;
        toCentre = ease((u - 0.5) / 0.22);
      } else if (u < 0.86) {
        toRing = 1;
        toCentre = 1;
      } else {
        back = ease((u - 0.86) / 0.14);
        toRing = 1 - back;
        toCentre = 1 - back;
      }

      const latticeA = (1 - toRing) * (u < 0.86 ? 1 : 1 - back) + back;

      // dot positions: lattice → ring → centre (with orbit spin on the ring)
      const pts: { x: number; y: number }[] = [];
      for (let i = 0; i < N; i++) {
        let x = lerp(lattice[i].x, ring[i].x, toRing);
        let y = lerp(lattice[i].y, ring[i].y, toRing);
        if (toRing > 0.001) {
          const a = -Math.PI / 2 + (i / N) * Math.PI * 2 + spin;
          const rx = Math.cos(a) * RR,
            ry = Math.sin(a) * RR;
          x = lerp(x, rx, toRing);
          y = lerp(y, ry, toRing);
        }

        x = lerp(x, 0, toCentre);
        y = lerp(y, 0, toCentre);
        pts.push({ x, y });
      }

      // X-lattice lines (fade with lattice)
      if (latticeA > 0.01) {
        ctx.strokeStyle = "rgba(" + rgb + "," + 0.5 * latticeA + ")";
        ctx.lineWidth = 1;
        for (const [p, q] of diagonals) {
          ctx.beginPath();
          ctx.moveTo(pts[p].x, pts[p].y);
          ctx.lineTo(pts[q].x, pts[q].y);
          ctx.stroke();
        }
      }

      // orbit ring outline
      const ringOutline = Math.max(toRing, 0);
      if (ringOutline > 0.01) {
        const rad = lerp(RR, RR * 0.62, toCentre);
        ctx.beginPath();
        ctx.arc(0, 0, rad, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(" + rgb + "," + 0.85 * ringOutline + ")";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // dots (fade out as they merge to centre)
      const dotA = 1 - toCentre;
      for (let i = 0; i < N; i++) {
        ctx.beginPath();
        ctx.fillStyle = "rgba(" + rgb + "," + 0.9 * Math.max(dotA, 0) + ")";
        ctx.arc(pts[i].x, pts[i].y, 2.1, 0, Math.PI * 2);
        ctx.fill();
      }

      // centre dot (appears as they collapse)
      if (toCentre > 0.01) {
        ctx.beginPath();
        ctx.fillStyle = "rgba(" + rgb + ",1)";
        ctx.arc(0, 0, 2.6 * toCentre, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size, color]);
  return (
    <canvas
      ref={ref}
      style={{ width: size, height: size, display: "block" }}
      aria-hidden
    />
  );
}
